import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Friend {
  id: string;
  friendId: string;
  friendName: string;
  friendAvatar: string | null;
  status: 'pending' | 'accepted' | 'rejected' | 'blocked';
  isOutgoing: boolean;
}

export function useFriendships(userId: string | undefined) {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<Friend[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<Friend[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [friendIds, setFriendIds] = useState<string[]>([]);

  const fetchFriendships = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      // Get all friendships where user is involved
      const { data: friendships, error } = await supabase
        .from('friendships')
        .select('*')
        .or(`user_id.eq.${userId},friend_id.eq.${userId}`);

      if (error) throw error;

      // Get profile data for all friends
      const friendUserIds = new Set<string>();
      friendships?.forEach(f => {
        if (f.user_id !== userId) friendUserIds.add(f.user_id);
        if (f.friend_id !== userId) friendUserIds.add(f.friend_id);
      });

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name, display_name, avatar_url')
        .in('id', Array.from(friendUserIds));

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      const mappedFriends: Friend[] = [];
      const mappedPending: Friend[] = [];
      const mappedOutgoing: Friend[] = [];
      const mappedBlocked: Friend[] = [];
      const acceptedFriendIds: string[] = [];

      friendships?.forEach(f => {
        const isOutgoing = f.user_id === userId;
        const otherUserId = isOutgoing ? f.friend_id : f.user_id;
        const profile = profileMap.get(otherUserId);

        const friendData: Friend = {
          id: f.id,
          friendId: otherUserId,
          friendName: profile?.display_name || profile?.name || 'Evolver',
          friendAvatar: profile?.avatar_url || null,
          status: f.status as Friend['status'],
          isOutgoing,
        };

        if (f.status === 'accepted') {
          mappedFriends.push(friendData);
          acceptedFriendIds.push(otherUserId);
        } else if (f.status === 'pending') {
          if (isOutgoing) {
            mappedOutgoing.push(friendData);
          } else {
            mappedPending.push(friendData);
          }
        } else if (f.status === 'blocked' && isOutgoing) {
          mappedBlocked.push(friendData);
        }
      });

      setFriends(mappedFriends);
      setPendingRequests(mappedPending);
      setOutgoingRequests(mappedOutgoing);
      setBlockedUsers(mappedBlocked);
      setFriendIds(acceptedFriendIds);
    } catch (error) {
      console.error('Error fetching friendships:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchFriendships();
  }, [fetchFriendships]);

  // Subscribe to realtime friend notifications
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('friendships-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'friendships',
          filter: `friend_id=eq.${userId}`,
        },
        async (payload) => {
          // Someone sent you a friend request
          const newFriendship = payload.new as any;
          if (newFriendship.user_id !== userId && newFriendship.status === 'pending') {
            // Fetch the friend's profile to get their name
            const { data: profile } = await supabase
              .from('profiles')
              .select('name, display_name')
              .eq('id', newFriendship.user_id)
              .single();
            
            const friendName = profile?.display_name || profile?.name || 'Someone';
            toast.success(`${friendName} sent you a friend request!`, {
              icon: '👋',
            });
            fetchFriendships();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'friendships',
          filter: `user_id=eq.${userId}`,
        },
        async (payload) => {
          // Your request was accepted/rejected
          const updated = payload.new as any;
          if (updated.status === 'accepted') {
            const { data: profile } = await supabase
              .from('profiles')
              .select('name, display_name')
              .eq('id', updated.friend_id)
              .single();
            
            const friendName = profile?.display_name || profile?.name || 'Someone';
            toast.success(`${friendName} accepted your friend request!`, {
              icon: '🤝',
            });
            fetchFriendships();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, fetchFriendships]);

  const sendFriendRequest = async (referralCode: string) => {
    if (!userId) return false;

    try {
      // Find user by referral code
      const { data: profile, error: findError } = await supabase
        .from('profiles')
        .select('id, name, display_name')
        .eq('referral_code', referralCode.toUpperCase())
        .single();

      if (findError || !profile) {
        toast.error('No user found with that code');
        return false;
      }

      if (profile.id === userId) {
        toast.error("You can't add yourself as a friend!");
        return false;
      }

      // Check if friendship already exists
      const { data: existing } = await supabase
        .from('friendships')
        .select('id, status')
        .or(`and(user_id.eq.${userId},friend_id.eq.${profile.id}),and(user_id.eq.${profile.id},friend_id.eq.${userId})`)
        .maybeSingle();

      if (existing) {
        if (existing.status === 'accepted') {
          toast.info('You are already friends!');
        } else if (existing.status === 'pending') {
          toast.info('Friend request already pending');
        } else if (existing.status === 'blocked') {
          toast.error('Unable to send friend request');
        }
        return false;
      }

      // Create pending friend request
      const { error: insertError } = await supabase
        .from('friendships')
        .insert({
          user_id: userId,
          friend_id: profile.id,
          status: 'pending',
        });

      if (insertError) throw insertError;

      toast.success(`Friend request sent to ${profile.display_name || profile.name || 'Evolver'}!`);
      fetchFriendships();
      return true;
    } catch (error: any) {
      console.error('Error sending friend request:', error);
      toast.error('Failed to send friend request');
      return false;
    }
  };

  const acceptRequest = async (friendshipId: string) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .update({ status: 'accepted' })
        .eq('id', friendshipId);

      if (error) throw error;

      toast.success('Friend request accepted!');
      fetchFriendships();
      return true;
    } catch (error) {
      console.error('Error accepting request:', error);
      toast.error('Failed to accept request');
      return false;
    }
  };

  const rejectRequest = async (friendshipId: string) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('id', friendshipId);

      if (error) throw error;

      toast.success('Friend request declined');
      fetchFriendships();
      return true;
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Failed to decline request');
      return false;
    }
  };

  const blockUser = async (friendshipId: string, friendId: string) => {
    if (!userId) return false;

    try {
      // Delete any existing friendship
      await supabase
        .from('friendships')
        .delete()
        .eq('id', friendshipId);

      // Create a blocked entry
      const { error } = await supabase
        .from('friendships')
        .insert({
          user_id: userId,
          friend_id: friendId,
          status: 'blocked',
        });

      if (error) throw error;

      toast.success('User blocked');
      fetchFriendships();
      return true;
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Failed to block user');
      return false;
    }
  };

  const unblockUser = async (friendshipId: string) => {
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('id', friendshipId);

      if (error) throw error;

      toast.success('User unblocked');
      fetchFriendships();
      return true;
    } catch (error) {
      console.error('Error unblocking user:', error);
      toast.error('Failed to unblock user');
      return false;
    }
  };

  const removeFriend = async (friendshipId: string, friendId: string) => {
    if (!userId) return false;

    try {
      // Delete both directions of the friendship
      await supabase
        .from('friendships')
        .delete()
        .eq('id', friendshipId);

      await supabase
        .from('friendships')
        .delete()
        .or(`and(user_id.eq.${userId},friend_id.eq.${friendId}),and(user_id.eq.${friendId},friend_id.eq.${userId})`);

      toast.success('Friend removed');
      fetchFriendships();
      return true;
    } catch (error) {
      console.error('Error removing friend:', error);
      toast.error('Failed to remove friend');
      return false;
    }
  };

  // Legacy support - alias for sendFriendRequest
  const addFriendByCode = sendFriendRequest;

  return {
    friends,
    pendingRequests,
    outgoingRequests,
    blockedUsers,
    friendIds,
    loading,
    sendFriendRequest,
    addFriendByCode,
    acceptRequest,
    rejectRequest,
    blockUser,
    unblockUser,
    removeFriend,
    refetch: fetchFriendships,
  };
}
